import { Callback1, Callback2, VoidCallback1 } from '@deqo/foundation';
import { injectable } from 'inversify';
import { ReadStore } from './ReadStore';

@injectable()
export class Composition<T> extends ReadStore<Set<T>> {

    protected state = new Set<T>();

    public get size() {
        return this.state.size;
    }

    public add(...items: T[]): void {
        this.addAll(items);
    }

    public addAll(items: Iterable<T>): void {
        this.mutate(next => {
            for (const tm of items) {
                next.add(tm);
            }
        });
    }

    public remove(...items: T[]): void {
        this.mutate(next => {
            for (const tm of items) {
                next.delete(tm);
            }
        });
    }

    public set(all: Iterable<T>): void {
        this.mutateTo(new Set(all));
    }

    public clear(): void {
        this.mutate(next => next.clear());
    }

    public contains(child: T): boolean {
        return this.state.has(child);
    }

    public split(primaryPredicate: Callback1<boolean, T>): [T[], T[]] {
        const p = [] as T[];
        const s = [] as T[];
        for (const x of this.state) {
            (primaryPredicate(x) ? p : s).push(x);
        }
        return [p, s];
    }

    public every(callback: Callback1<boolean, T>): boolean {
        return this.find(callback) === null;
    }

    public find(callback: Callback1<boolean, T>): T | undefined {
        for (const x of this.state) {
            if (callback(x)) {
                return x;
            }
        }
    }

    public filter(callback: Callback1<boolean, T>): T[] {
        const arr = [] as T[];
        for (const x of this.state) {
            if (callback(x)) {
                arr.push(x);
            }
        }
        return arr;
    }

    public map<R>(callback: Callback1<R, T>): R[] {
        const arr = [] as R[];
        for (const x of this.state) {
            arr.push(callback(x));
        }
        return arr;
    }

    public reduce<R>(callback: Callback2<R, R, T>, initial: R): R {
        let current = initial;
        for (const x of this.state) {
            current = callback(current, x);
        }
        return current;
    }

    public forEach(callback: VoidCallback1<T>): void {
        return this.state.forEach(callback);
    }

    public toArray(): T[] {
        return Array.from(this.state);
    }

    public toSet(): Set<T> {
        return new Set<T>(this.state);
    }

    public [Symbol.iterator](): Iterator<T, any, undefined> {
        return this.state[Symbol.iterator]();
    }
}

